import React, { useEffect, useRef, useState } from 'react'
import { FaArrowDown } from 'react-icons/fa'
import styled from 'styled-components'

import { useRequestsByHouseId } from '../../../api/hooks/use.request'
import { MapPoint } from '../../../api/service/request.service'
import { AppealCardItem } from '../appeal.card.item.component'

interface MapProps {
	allPoints: MapPoint[]
	period: string
	selectedProblemId?: number
	isLoading?: boolean
}

const Wrapper = styled.div`
	width: 100%;
`

const MapOverlay = styled.div`
	position: absolute;
	inset: 0;
	background: rgba(255, 255, 255, 0.6);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	border-radius: 12px;
`

const Spinner = styled.div`
	width: 40px;
	height: 40px;
	border: 4px solid #e0e0e0;
	border-top-color: #01924f;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}
`

const MapContainer = styled.div`
	width: 100%;
	height: 500px;
	border-radius: 12px;
	overflow: hidden;
	z-index: 0;
	position: relative;
`
const ListSection = styled.div`
	margin-top: 30px;
	margin-bottom: 30px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`
const EmptyState = styled.div`
	font-family: 'e-Ukraine', sans-serif;
	font-size: 14px;
	color: #aaa;
	text-align: center;
	padding: 32px 0;
`
const LoadingState = styled.div`
	font-family: 'e-Ukraine', sans-serif;
	font-size: 14px;
	color: #aaa;
	text-align: center;
	padding: 32px 0;
`

const LoadMoreContainer = styled.div`
	padding: 15px 0;
	width: 100%;
	justify-content: center;
	display: flex;
`

const LoadMoreButton = styled.button`
	color: #01924f;
	border: none;
	gap: 10px;
	background: none;
	display: flex;
	justify-content: center;
	padding: 10px 24px;
	font-family: 'e-Ukraine', sans-serif;
	font-weight: 500;
	font-size: 15px;
	line-height: 122%;
	letter-spacing: 0.01em;
	cursor: pointer;
	transition: all 0.2s ease;

	&:disabled {
		color: #bfbfbf;
		cursor: not-allowed;
	}
`
const getMarkerColor = (hasInWork: number) =>
	hasInWork === 1 ? '#f5a623' : '#01924f'
const getMarkerBorder = (hasInWork: number) =>
	hasInWork === 1 ? '#d48800' : '#006b36'

const getClusterColor = (total: number) => {
	if (total >= 1000) return '#d93b10'
	if (total >= 500) return '#e84e1b'
	if (total >= 100) return '#f07020'
	if (total >= 30) return '#f5a623'
	if (total >= 10) return '#f5c842'
	return '#8dc63f'
}

const getClusterBorder = (total: number) => {
	if (total >= 1000) return '#b02500'
	if (total >= 500) return '#c03500'
	if (total >= 100) return '#d05500'
	if (total >= 30) return '#d48800'
	if (total >= 10) return '#d4a800'
	return '#6aaa1a'
}

export const RequestsMapComponent: React.FC<MapProps> = ({
	allPoints,
	period,
	selectedProblemId,
	isLoading,
}) => {
	const mapRef = useRef<HTMLDivElement>(null)
	const mapInstanceRef = useRef<any>(null)
	const markerLayerRef = useRef<any>(null)
	const [isMapReady, setIsMapReady] = useState(false)
	const [selectedPoint, setSelectedPoint] = useState<MapPoint | null>(null)

	useEffect(() => {
		setSelectedPoint(null)
	}, [period, selectedProblemId])

	const {
		data,
		isLoading: isLoadingAppeals,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
	} = useRequestsByHouseId(selectedPoint?.houseId, selectedProblemId, period)

	const appeals = data ? data.pages.flatMap(page => page.data) : []

	useEffect(() => {
		if (
			!isMapReady ||
			!mapInstanceRef.current ||
			!(window as any).L ||
			!(window as any).L.markerClusterGroup
		)
			return
		const L = (window as any).L

		if (markerLayerRef.current) {
			markerLayerRef.current.clearLayers()
			mapInstanceRef.current.removeLayer(markerLayerRef.current)
			markerLayerRef.current = null
		}

		const clusterGroup = L.markerClusterGroup({
			maxClusterRadius: 60,
			// Кастомная иконка кластера — суммирует count всех вошедших маркеров
			iconCreateFunction: (cluster: any) => {
				const markers = cluster.getAllChildMarkers()
				// Суммируем count из options каждого маркера
				const totalCount = markers.reduce(
					(sum: number, m: any) => sum + (m.options.pointCount || 0),
					0,
				)
				const color = getClusterColor(totalCount)
				const border = getClusterBorder(totalCount)
				const size =
					totalCount >= 100
						? 46
						: totalCount >= 30
							? 42
							: totalCount >= 10
								? 38
								: 34

				return L.divIcon({
					className: '',
					html: `<div style="
            background:${color};
            color:white;
            border-radius:50%;
            width:${size}px;
            height:${size}px;
            display:flex;
            align-items:center;
            justify-content:center;
            font-size:13px;
            font-weight:700;
            font-family:sans-serif;
            box-shadow:0 2px 8px rgba(0,0,0,0.25);
            border:3px solid ${border};
          ">${totalCount}</div>`,
					iconSize: [size, size],
					iconAnchor: [size / 2, size / 2],
				})
			},
		})

		allPoints.forEach(point => {
			const { lat, lon, count, hasInWork } = point

			const color = getMarkerColor(hasInWork)
			const border = getMarkerBorder(hasInWork)
			const size = count > 1 ? 40 : 43

			const icon = L.divIcon({
				className: '',
				html: `<div style="
          background:${color};
          color:white;
          border-radius:50%;
          width:${size}px;
          height:${size}px;
          display:flex;
          align-items:center;
          justify-content:center;
          font-size:11px;
          font-weight:700;
          font-family:sans-serif;
          box-shadow:0 2px 6px rgba(0,0,0,0.25);
          border:2px solid ${border};
        ">${count}</div>`,
				iconSize: [size, size],
				iconAnchor: [size / 2, size / 2],
			})

			const marker = L.marker([lat, lon], { icon, pointCount: count })

			marker.on('click', () => {
				const currentZoom = mapInstanceRef.current.getZoom()
				if (currentZoom < 15) {
					mapInstanceRef.current.setView([lat, lon], 15, {
						animate: true,
						duration: 0.8,
					})
				} else {
					mapInstanceRef.current.panTo([lat, lon], {
						animate: true,
						duration: 0.5,
					})
				}
				setSelectedPoint(point)
			})

			clusterGroup.addLayer(marker)
		})

		mapInstanceRef.current.addLayer(clusterGroup)
		markerLayerRef.current = clusterGroup
	}, [allPoints, isMapReady])

	useEffect(() => {
		if (!mapRef.current || mapInstanceRef.current) return

		let isMounted = true

		const loadLeaflet = async () => {
			if (!document.querySelector('#leaflet-css')) {
				const link = document.createElement('link')
				link.id = 'leaflet-css'
				link.rel = 'stylesheet'
				link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
				document.head.appendChild(link)
			}
			if (!document.querySelector('#leaflet-cluster-css')) {
				const link = document.createElement('link')
				link.id = 'leaflet-cluster-css'
				link.rel = 'stylesheet'
				link.href =
					'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css'
				document.head.appendChild(link)
			}
			if (!document.querySelector('#leaflet-cluster-default-css')) {
				const link = document.createElement('link')
				link.id = 'leaflet-cluster-default-css'
				link.rel = 'stylesheet'
				link.href =
					'https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css'
				document.head.appendChild(link)
			}

			if (!(window as any).L) {
				await new Promise<void>((resolve, reject) => {
					const script = document.createElement('script')
					script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js'
					script.async = true
					script.onload = () => resolve()
					script.onerror = () => reject(new Error('Leaflet failed to load'))
					document.head.appendChild(script)
				})
			}

			if ((window as any).L && !(window as any).L.markerClusterGroup) {
				await new Promise<void>((resolve, reject) => {
					const script = document.createElement('script')
					script.src =
						'https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js'
					script.async = true
					script.onload = () => resolve()
					script.onerror = () =>
						reject(new Error('MarkerCluster failed to load'))
					document.head.appendChild(script)
				})
			}

			if (!isMounted || !mapRef.current) return
			if (mapRef.current.classList.contains('leaflet-container')) return

			const L = (window as any).L
			const map = L.map(mapRef.current, {
        center: [47.8388, 35.1396],
        zoom: 12,           
        minZoom: 9,         
        maxZoom: 19,
        maxBounds: [
          [46.8, 33.8],     
          [48.5, 36.8],
        ],
        maxBoundsViscosity: 1.0,
      })
			L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
				attribution: '© OpenStreetMap',
				maxZoom: 19,
			}).addTo(map)

			mapInstanceRef.current = map
			setIsMapReady(true)
		}

		loadLeaflet().catch(err => console.error('Map init error:', err))

		return () => {
			isMounted = false
			if (markerLayerRef.current && mapInstanceRef.current) {
				markerLayerRef.current.clearLayers()
				mapInstanceRef.current.removeLayer(markerLayerRef.current)
				markerLayerRef.current = null
			}
			if (mapInstanceRef.current) {
				mapInstanceRef.current.remove()
				mapInstanceRef.current = null
				setIsMapReady(false)
			}
		}
	}, [])

	return (
		<Wrapper>
			<MapContainer ref={mapRef}>
				{isLoading && (
					<MapOverlay>
						<Spinner />
					</MapOverlay>
				)}
			</MapContainer>

			<ListSection>
				{selectedPoint && isLoadingAppeals && (
					<LoadingState>Завантаження...</LoadingState>
				)}

				{selectedPoint && !isLoadingAppeals && appeals.length === 0 && (
					<EmptyState>Звернення не знайдено</EmptyState>
				)}

				{selectedPoint &&
					appeals.map(appeal => (
						<AppealCardItem key={appeal.id} appeal={appeal} />
					))}

				{selectedPoint && hasNextPage && (
					<LoadMoreContainer>
						<LoadMoreButton
							onClick={() => fetchNextPage()}
							disabled={isFetchingNextPage}
						>
							{isFetchingNextPage ? (
								'Завантаження...'
							) : (
								<>
									<FaArrowDown size={18} /> Показати ще
								</>
							)}
						</LoadMoreButton>
					</LoadMoreContainer>
				)}
			</ListSection>
		</Wrapper>
	)
}
